import { ref } from 'vue';

import Axios, { AxiosError, AxiosResponse } from 'axios';
import { alertController } from '@ionic/vue';
import useLocalStorage from '@/composeables/useLocalStorage';
import { version } from '../../package.json';
const cachedResponses = {} as any;
const doNotCacheUrls = ['/me'];
const lastError = ref(null) as any;
const lastErrorCode = ref<number | null>(null);
const lastResponse = ref(null) as any;
const pending = ref(false);
const defaultRequestTimeout = 20000;

type ApiErrorData = {
  error?: string;
  message?: string;
  errors?: Record<string, string[] | string | null | undefined>;
};

const flattenValidationErrors = (errors?: ApiErrorData['errors']): string[] => {
  if (!errors || typeof errors !== 'object') {
    return [];
  }

  return Object.values(errors)
    .flatMap(value => {
      if (Array.isArray(value)) {
        return value.filter((item): item is string => typeof item === 'string' && item.trim().length > 0);
      }

      if (typeof value === 'string' && value.trim().length > 0) {
        return [value];
      }

      return [];
    });
};

const extractApiErrorMessage = (data?: ApiErrorData): string | null => {
  if (!data) {
    return null;
  }

  const validationMessages = flattenValidationErrors(data.errors);
  if (validationMessages.length > 0) {
    return validationMessages.join('\n');
  }

  if (typeof data.message === 'string' && data.message.trim().length > 0) {
    return data.message;
  }

  if (typeof data.error === 'string' && data.error.trim().length > 0) {
    return data.error;
  }

  return null;
};

export default function useApi() {
  const apiGet = async<T>(url: string, additionalHeaders? : null | object, fromCache = true, staleTimer?: number) : Promise<T> => {
    return req<T>(url, 'GET', null, additionalHeaders, fromCache, staleTimer);
  };

  const apiPost = async<T>(url: string, body?: null | object, additionalHeaders? : null | object, fromCache = false) : Promise<T> => {
    return req<T>(url, 'POST', body, additionalHeaders, fromCache);
  };

  const apiPut = async<T>(url: string, body?: null | object, additionalHeaders? : null | object, fromCache = false) : Promise<T> => {
    return req<T>(url, 'PUT', body, additionalHeaders, fromCache);
  };

  const apiDelete = async<T>(url: string, additionalHeaders? : null | object) : Promise<T> => {
    return req<T>(url, 'DELETE', null, additionalHeaders, false);
  };

  const req = async<T>(url: string, method: string, body?: null | object, additionalHeaders? : null | object, fromCache = true, staleTimer?: number) : Promise<AxiosResponse<T> | any> => {
    const storage = await useLocalStorage();
    const accountToken = await storage.get('accountToken');
    pending.value = true;
    const cacheKey = url + (body ? JSON.stringify(body) : '');
    if (fromCache && cachedResponses[cacheKey]?.data && (staleTimer ? cachedResponses[cacheKey]?.timestamp + staleTimer >= new Date().getTime() : true)) {
      pending.value = false;

      return cachedResponses[cacheKey].data as T;
    }
    lastResponse.value = null;
    lastError.value = null;
    lastErrorCode.value = null;

    try {
      const opts = {
        headers: {
          'Accept': 'application/json',
          'Svenskhandel-App-Version': version,
        },
        baseURL: import.meta.env.VITE_API_URL,
        method: method,
        timeout: defaultRequestTimeout,
      } as any;

      if (accountToken) {
        opts.headers.Authorization = `Bearer ${accountToken}`;
      }
      if (body) {
        opts.data = body;
      }
      if (additionalHeaders) {
        opts.headers = Object.assign(opts.header, additionalHeaders);
      }

      const res = await Axios<T>({
        url,
        ...opts,
      });

      if (!doNotCacheUrls.some(doNotCache => url.includes(doNotCache))) {
        cachedResponses[cacheKey] = {
          data: res.data,
          timestamp: new Date().getTime(),
        };
      }

      lastResponse.value = res.data;

      return res.data as T;

    } catch (e: unknown) {
      const error = e as AxiosError<ApiErrorData>;
      lastError.value = extractApiErrorMessage(error?.response?.data);
      lastErrorCode.value = error?.response?.status || 0;
      return null;
    } finally {
      pending.value = false;
    }
  };

  const handleApiError = async(
    header = 'Någonting gick fel',
    message = 'Försök igen eller kontakta support'
  ) => {
    console.error(lastError.value);
    console.warn('Code: ' + lastErrorCode.value);
    if (lastErrorCode.value === 401) {
      const alert = await alertController.create({
        header: 'Fel',
        subHeader: 'Du har inte behörighet att göra detta',
        message: 'Om du tror att detta är ett fel, kontakta administratör',
        buttons: [ 'OK' ],
      });
      await alert.present();
    } else {
      const defaultMessage = message === 'Försök igen eller kontakta support';
      const shouldUseBackendError = lastErrorCode.value === 422 || defaultMessage;
      const resolvedMessage = shouldUseBackendError && lastError.value ? lastError.value : message;

      const alert = await alertController.create({
        header,
        message: resolvedMessage,
        buttons: [ 'OK' ],
      });
      await alert.present();
    }
  };

  return {
    apiGet,
    apiPost,
    apiPut,
    apiDelete,
    lastError,
    lastErrorCode,
    lastResponse,
    pending,
    handleApiError,
  };
}
